import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Flame, AlertCircle, Globe } from 'lucide-react';
import './SwedenSlide.css';

// Import images
import rasmusPaludanImg from '../images/images for slides/Rasmus Paludan.jpg';
import muslimProtestImg from '../images/images for slides/muslims protest with them holding qurans.webp';

function SwedenSlide({ showNotes }) {
  const [activeStep, setActiveStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const cycleSteps = [
    { 
      number: 1, 
      icon: '📢', 
      title: 'Ankündigung',
      text: 'Paludan kündigt Koranverbrennung vor einer Moschee an'
    },
    { 
      number: 2, 
      icon: '⚖️', 
      title: 'Legalität',
      text: 'Verbrennung ist laut schwedischem Gesetz legale Meinungsfreiheit'
    },
    { 
      number: 3, 
      icon: '😡', 
      title: 'Reaktion',
      text: 'Wut und Ausschreitungen in der muslimischen Community'
    },
    { 
      number: 4, 
      icon: '📹', 
      title: 'Dokumentation',
      text: 'Paludan filmt die Gewalt'
    },
    { 
      number: 5, 
      icon: '📣', 
      title: 'Narrativ',
      text: '"Seht her, Muslime sind gewalttätig"'
    },
    { 
      number: 6, 
      icon: '📈', 
      title: 'Wachstum',
      text: 'Mehr Aufmerksamkeit, mehr Anhänger → zurück zu Schritt 1'
    },
  ];

  const globalConsequences = [
    { icon: '🇹🇷', text: 'Diplomatische Krise mit der Türkei' },
    { icon: '🛡️', text: 'Schwedens NATO-Beitritt wurde monatelang blockiert' },
    { icon: '🌍', text: 'Proteste und Gewalt vor schwedischen Botschaften weltweit' },
  ];

  useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(() => {
        setActiveStep((prev) => (prev + 1) % cycleSteps.length);
      }, 4000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, cycleSteps.length]);

  const handleStepClick = (index) => {
    setActiveStep(index);
    if (index === 0) {
      setIsPlaying(true);
    } else {
      setIsPlaying(false);
    }
  };

  return (
    <div className="slide sweden-slide">
      <div className="sweden-layout">
        {/* Header */}
        <div className="sweden-header">
          <motion.span
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="section-label"
          >
            🇸🇪 Schweden
          </motion.span>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="slide-title"
          >
            Provokation als Waffe
          </motion.h2>
        </div>

        <div className="sweden-content">
          {/* Left Column - Actor & Image */}
          <div className="sweden-left">
            {/* Actor Card */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="actor-card"
            >
              <div className="actor-image">
                <img src={rasmusPaludanImg} alt="Rasmus Paludan" className="actor-photo-img" />
              </div>
              <div className="actor-info">
                <h3>Rasmus Paludan</h3>
                <p className="actor-desc">Dänisch-schwedischer Rechtsextremist</p>
                <div className="actor-action">
                  <Flame size={16} className="flame-icon" />
                  <span>Öffentliche Koranverbrennungen in Migrantenvierteln</span>
                </div>
              </div>
            </motion.div>

            {/* Image placeholder for event */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="event-image"
            >
              <img src={muslimProtestImg} alt="Proteste bei Koranverbrennung" className="event-photo" />
            </motion.div>

            {/* Global Consequences */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="consequences-box"
            >
              <div className="consequences-header">
                <Globe size={18} />
                <span>Globale Folgen</span>
              </div>
              <div className="consequences-list">
                {globalConsequences.map((item, index) => (
                  <div key={index} className="consequence-item">
                    <span className="consequence-icon">{item.icon}</span>
                    <span className="consequence-text">{item.text}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Right Column - The Cycle */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="sweden-right"
          >
            <div className="cycle-header">
              <AlertCircle size={20} className="trap-icon" />
              <h3>Die Falle – Ein Kreislauf</h3>
            </div>

            <div className="cycle-container">
              <div className="cycle-visual">
                {cycleSteps.map((step, index) => (
                  <motion.div
                    key={index}
                    className={`cycle-step ${activeStep === index ? 'active' : ''}`}
                    onClick={() => handleStepClick(index)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4 + index * 0.08 }}
                  >
                    <div className="step-number">{step.number}</div>
                    <div className="step-icon">{step.icon}</div>
                    <div className="step-title">{step.title}</div>
                  </motion.div>
                ))}
                
                {/* Central Arrow Animation */}
                <div className="cycle-center">
                  <motion.div
                    className="cycle-arrows"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  >
                    ↻
                  </motion.div>
                </div>
              </div>

              {/* Active Step Detail */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeStep}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="step-detail"
                >
                  <div className="detail-header">
                    <span className="detail-icon">{cycleSteps[activeStep].icon}</span>
                    <span className="detail-number">Schritt {cycleSteps[activeStep].number}</span>
                  </div>
                  <p className="detail-text">{cycleSteps[activeStep].text}</p>
                  {isPlaying && (
                    <div className="timer-container">
                      <motion.div 
                        className="timer-bar"
                        initial={{ width: "0%" }}
                        animate={{ width: "100%" }}
                        transition={{ duration: 4, ease: "linear" }}
                      />
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              {/* Auto-play hint */}
              <p className="cycle-hint">
                {isPlaying ? 'Automatik-Modus aktiv...' : 'Klicke auf Schritt 1 für Automatik-Modus'}
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {showNotes && (
        <div className="speaker-notes">
          <strong>📚 Zusatzinfos für Leser:</strong><br/>
          <strong>Der "System-Hack":</strong> Paludan nutzt demokratische Gesetze (Meinungsfreiheit), um die Demokratie zu destabilisieren. 
          Er sucht keine Debatte, sondern produziert Bilder für Social Media.<br/><br/>
          <strong>Der Ablauf:</strong><br/>
          1. Provokation in muslimischen Vierteln → legal.<br/>
          2. Gegenproteste eskalieren → Paludan filmt und inszeniert sich als Opfer.<br/>
          3. Export des Chaos → Türkische Blockade des NATO-Beitritts.<br/><br/>
          <strong>Die Strategie:</strong> Eine Einzelperson kann durch Ausnutzen liberaler Gesetze die Sicherheitsarchitektur eines ganzen Kontinents (NATO) als Geisel nehmen.
        </div>
      )}
    </div>
  );
}

export default SwedenSlide;
